// import QRCodeStyling from "qr-code-styling";
// import qrStyleOptions from "../styles/qr-styling.json";

interface WatermarkOptions {
    height?: number;
    heightPercent?: number;
    logoSize?: number;
    // qrUrl?: string;
    // maxSize?: number;
    // maxSizePercent?: number;
}

/**
 * Creates a watermark with Rapid logo spanning full width
 * @param imageWidth Width of the target image
 * @param imageHeight Height of the target image
 * @param options Watermark options
 * @returns Canvas element with watermark or null if failed
 */
export async function createWatermark(
    imageWidth: number,
    imageHeight: number,
    options: WatermarkOptions = {}
): Promise<HTMLCanvasElement | null> {
    const { height = 80, heightPercent = 0.4, logoSize } = options;
    // const { qrUrl = "https://rapid.roboflow.com", maxSize = 300, maxSizePercent = 0.2 } = options;

    try {
        // Calculate watermark height
        const percentHeight = imageHeight * heightPercent;
        const finalHeight = Math.round(Math.min(height, percentHeight));
        const finalWidth = imageWidth;

        // Calculate logo size as 8% of image height, with fallback to provided logoSize
        const calculatedLogoSize = Math.round(imageHeight * 0.08);
        const finalLogoSize = logoSize || calculatedLogoSize;

        // QR Code implementation (commented out)
        /*
        try {
            // Calculate final watermark size
            const percentSize = Math.min(imageWidth, imageHeight) * maxSizePercent;
            const finalSize = Math.round(Math.min(maxSize, percentSize));
            // Create QR code with styling options
            // Override the imageSize to make the logo bigger
            const qrCode = new QRCodeStyling({
                ...qrStyleOptions,
                width: finalSize,
                height: finalSize,
                data: qrUrl,
                type: "canvas",
                imageOptions: { ...qrStyleOptions.imageOptions }
            });

            // Create a temporary container for the QR code
            const container = document.createElement("div");
            container.style.position = "absolute";
            container.style.left = "-9999px";
            container.style.top = "-9999px";
            document.body.appendChild(container);

            // Append QR code to container to render it
            qrCode.append(container);

            // Wait for the QR code and logo image to fully render
            await new Promise((resolve) => setTimeout(resolve, 200));

            // Get the canvas element that was created
            const qrCanvas = container.querySelector("canvas");

            if (!qrCanvas) {
                document.body.removeChild(container);
                throw new Error("Failed to generate QR code canvas");
            }
            // Create a new canvas with the QR code
            const canvas = document.createElement("canvas");
            canvas.width = finalSize;
            canvas.height = finalSize;
            const ctx = canvas.getContext("2d");

            if (!ctx) {
                document.body.removeChild(container);
                throw new Error("Failed to get canvas context");
            }

            // Copy the QR code to our canvas
            ctx.drawImage(qrCanvas, 0, 0, finalSize, finalSize);

            // Clean up the temporary container
            document.body.removeChild(container);

            return canvas;
        } catch (error) {
            console.error("Error creating QR code watermark:", error);
            return null;
        }
        */

        // Load the Rapid logo SVG
        const logoResponse = await fetch("/images/rapid-logo-white.svg");
        if (!logoResponse.ok) {
            throw new Error("Failed to load Rapid logo");
        }
        const logoSvg = await logoResponse.text();

        // Create a new canvas for the watermark spanning full width
        const canvas = document.createElement("canvas");
        canvas.width = finalWidth;
        canvas.height = finalHeight;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
            throw new Error("Failed to get canvas context");
        }

        // Create linear gradient from transparent white to gray-600 (top to bottom)
        const gradient = ctx.createLinearGradient(0, 0, 0, finalHeight);
        gradient.addColorStop(0, "rgba(255, 255, 255, 0.00)"); // transparent white
        gradient.addColorStop(1, "#4B5563"); // gray-600

        // Fill the entire canvas with the gradient
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, finalWidth, finalHeight);

        // Create an image from the SVG
        const img = new Image();
        const svgBlob = new Blob([logoSvg], { type: "image/svg+xml" });
        const url = URL.createObjectURL(svgBlob);

        return new Promise((resolve) => {
            img.onload = () => {
                // Calculate logo position with natural aspect ratio
                const logoAspectRatio = img.width / img.height;
                const logoHeight = finalLogoSize;
                const logoWidth = logoHeight * logoAspectRatio;

                // Position logo in the bottom right corner with percentage-based padding
                const paddingX = finalWidth * 0.02; // 2% of width
                const paddingY = finalHeight * 0.02; // 2% of height
                const logoX = finalWidth - logoWidth - paddingX;
                const logoY = finalHeight - logoHeight - paddingY;

                // Set logo opacity to 50%
                ctx.globalAlpha = 0.5;

                // Draw the logo with its natural aspect ratio
                ctx.drawImage(img, logoX, logoY, logoWidth, logoHeight);

                // Reset opacity
                ctx.globalAlpha = 1.0;

                // Clean up the object URL
                URL.revokeObjectURL(url);

                resolve(canvas);
            };

            img.onerror = () => {
                console.error("Failed to load logo image");
                URL.revokeObjectURL(url);
                resolve(null);
            };

            img.src = url;
        });
    } catch (error) {
        console.error("Error creating watermark:", error);
        return null;
    }
}

/**
 * Applies watermark to a canvas
 * @param targetCtx Target canvas context to draw watermark on
 * @param watermarkCanvas Watermark canvas to apply
 * @param canvasWidth Width of target canvas
 * @param canvasHeight Height of target canvas
 */
export function applyWatermarkToCanvas(
    targetCtx: CanvasRenderingContext2D,
    watermarkCanvas: HTMLCanvasElement,
    canvasWidth: number,
    canvasHeight: number
): void {
    // Position watermark at the bottom spanning full width
    const x = 0;
    const y = canvasHeight - watermarkCanvas.height;

    // Save current context state
    targetCtx.save();

    // Use better image smoothing
    targetCtx.imageSmoothingEnabled = true;
    targetCtx.imageSmoothingQuality = "high";

    // Draw the complete watermark (includes gradient background and logo)
    targetCtx.drawImage(watermarkCanvas, x, y);

    // Restore context state
    targetCtx.restore();
}
